import { DataGrid } from "@mui/x-data-grid/DataGrid";
import React, { useState, useEffect } from "react";
import {Box, Button, Snackbar, Typography, Drawer, Modal, Stack, Pagination} from "@mui/material";
import { GridRowId } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import "./Admin.css";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonIcon from '@mui/icons-material/Person';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from "@mui/icons-material/Add";
import { PartnerListItem } from "../../data/partnerData";
import { getPartnersList, deletePartner } from "../../services/partnerInformation";
import AddPartnerModal from "../partners/AddPartnerModal";
import EditPartnerModal from "../partners/EditPartnerModal";
import DeletePartnerModal from "../partners/DeletePartnerModal";


export default function Partner() {
    const navigate = useNavigate();
    const [partners, setPartners] = useState<PartnerListItem[]>([]);
    const [selectedRow, setSelectedRow] = useState<PartnerListItem | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarDeleteOpen, setSnackbarDeleteOpen] = useState(false);

    const handleSnackbarDeleteOpen = () => setSnackbarDeleteOpen(true);
    const handleSnackbarDeleteClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarDeleteOpen(false);
    };

    const getPartners = async () => {
        try {
            const partnersList = await getPartnersList();
            setPartners(partnersList);
        } catch (error) {
            console.error("Error fetching partners:", error);
        }
    };

    useEffect(() => {
        getPartners();
    }, []);

    const handleOpenAddModal = () => {
        setAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setAddModalOpen(false);
    };

    const handleDelete = () => {
        setDeleteModalOpen(true);
    };

    const handleManageUsers = (PartnerId: string, partnerName: string) => {
        navigate(`/partner-users/${PartnerId}`, {
            state: { partnerName }
        });
    };

    const handleSelectionModelChange = (selectionModel: GridRowId[]) => {
        if (selectionModel.length > 1) {
            alert("Please select only one row");
        } else {
            setSelectedRow(partners.find((row) => row.PartnerId === selectionModel[0]) || null);
        }
    };

    const columns = [
        {field: 'Name', headerName: 'Name', width: 240},
        {field: 'ContactName', headerName: 'Contact', width: 180},
        {
            field: 'moreOptions',
            headerName: '',
            width: 120,
            renderCell: (params: any) => (
                <Button
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRow(params.row);
                        setDrawerOpen(true);
                    }}
                    variant="text"
                    size="small"
                    endIcon={<MoreHorizIcon/>}
                    sx={{
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    More Options
                </Button>
            ),
        },
    ];

    const modalStyle = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const getCurrentItems = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return partners.slice(startIndex, endIndex);
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, p: 2}}>
            <Stack direction="row" spacing={2} justifyContent="flex-start">
                <Button variant="contained" className="editOrAdd" onClick={() => setAddModalOpen(true)} startIcon={<AddIcon />}>Add Partner</Button>
            </Stack>
            <Box sx={{
                display: 'flex',
                transition: 'all 0.3s ease',
                width: drawerOpen ? 'calc(100%)' : '100%',
                position: 'relative'
            }}>
                <Box sx={{
                    width: drawerOpen ? '60%' : '100%',
                    transition: 'width 0.3s ease'
                }}>
                    {partners.length > 0 ? (
                        <DataGrid
                            rows={getCurrentItems()}
                            columns={columns}
                            getRowId={(row) => row.PartnerId}
                            className="datagrid"
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooterPagination={true}
                        />
                    ) : (
                        <Box sx={{display: 'flex', alignContent: "center"}}>
                            <CircularProgress color="info"/>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        width: drawerOpen ? '40%' : 0,
                        transition: 'width 0.3s ease',
                        borderLeft: drawerOpen ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                        overflow: 'hidden'
                    }}
                >
                    {selectedRow && (
                        <Box sx={{p: 3}}>
                            <Typography variant="h6" gutterBottom>
                                View or Make Changes to {selectedRow.Name}
                            </Typography>
                            <Box sx={{mt: 2}}>
                                <Typography color="text.secondary" gutterBottom>
                                    Description
                                </Typography>
                                <Typography>{selectedRow.Description || 'No description available'}</Typography>
                            </Box>
                            <Box sx={{mt: 4}}>
                                <Typography variant="h6" gutterBottom>
                                    Contact Info for {selectedRow.ContactName || 'No contact'}
                                </Typography>
                                <Box sx={{mt: 2}}>
                                    <Typography color="text.secondary" gutterBottom>
                                        Email
                                    </Typography>
                                    <Typography>{selectedRow.EmailAddress}</Typography>
                                </Box>
                                <Box sx={{mt: 2}}>
                                    <Typography color="text.secondary" gutterBottom>
                                        Phone
                                    </Typography>
                                    <Typography>{selectedRow.ContactPhone}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{mt: 4, display: 'flex', gap: 2, flexDirection: 'column', alignItems: 'center'}}>
                                <Button
                                    variant="contained"
                                    className="editOrAdd"
                                    onClick={() => setEditModalOpen(true)}
                                    startIcon={<EditIcon/>}
                                    sx={{minWidth: '150px'}}
                                >
                                    Edit Partner
                                </Button>
                                <Button
                                    variant="contained"
                                    className="delete"
                                    onClick={handleDelete}
                                    startIcon={<DeleteIcon/>}
                                    sx={{minWidth: '150px'}}
                                >
                                    Delete
                                </Button>
                                <Button
                                    variant="contained"
                                    className="info"
                                    onClick={() => handleManageUsers(selectedRow.PartnerId, selectedRow.Name)}
                                    startIcon={<PersonIcon/>}
                                    sx={{minWidth: '150px'}}
                                >
                                    Manage Users for this Partner
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            <Pagination
                count={Math.ceil(partners.length / itemsPerPage)}
                page={currentPage}
                onChange={(e, page) => handlePageChange(page)}
                color="primary"
                className="pagination"
            />

            <EditPartnerModal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} partner={selectedRow} setSnackbarMessage={setSnackbarMessage} setSnackbarOpen={setSnackbarOpen} refreshPartners={getPartners} setDrawerOpen={setDrawerOpen}/>
            <AddPartnerModal isOpen={isAddModalOpen} onClose={() => setAddModalOpen(false)} setSnackbarMessage={setSnackbarMessage} setSnackbarOpen={setSnackbarOpen} refreshPartners={getPartners}/>
            <DeletePartnerModal isOpen={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)} partner={selectedRow} refreshPartners={getPartners} setSnackbarDeleteOpen={handleSnackbarDeleteOpen} setDrawerOpen={setDrawerOpen} setSelectedRow={setSelectedRow}/>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
            <Snackbar
                open={snackbarDeleteOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarDeleteClose}
                message="Partner information deleted successfully."
            />
        </Box>
    );
}